// src/pages/DashboardOverview.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './DashboardOverview.module.css';
import CreateListingModal from '../components/UI/CreateListingModal';
import AuthContext from '../context/AuthContext';
import FourCardGrid from '../components/DashboardCards/FourCardGrid';
import CatalogueHealthCard from '../components/DashboardCards/CatalogueHealthCard';
import RecentProjectsCard from '../components/DashboardCards/RecentProjectsCard';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'; // Fallback

const DashboardOverview = () => {
  const authCtx = useContext(AuthContext); // <<< Gets context
  const navigate = useNavigate();

  // Calculate initial projects count based on screen size
  const getProjectsCount = () => {
    const width = window.innerWidth;
    if (width >= 1920) {
      return 7;
    } else if (width >= 1790) {
      return 7;
    } else if (width >= 1850) {
      return 7;
    } else if (width >= 1280) {
      return 5;
    } else {
      return 5;
    }
  };

  const [isCreateListingModalOpen, setIsCreateListingModalOpen] = useState(false);
  const [isNewListingMode, setIsNewListingMode] = useState(true); // Track if modal is for new or existing listing
  const [modalInitialType, setModalInitialType] = useState(null); // 'new', 'existing', or null (null = show full flow)
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentProjectsLoading, setRecentProjectsLoading] = useState(true);
  const [projectsToShow, setProjectsToShow] = useState(getProjectsCount());

  const fullName = authCtx.user?.fullname|| "User";

  // Update number of projects to show based on screen size
  useEffect(() => {
    const updateProjectsCount = () => {
      setProjectsToShow(getProjectsCount());
    };

    window.addEventListener('resize', updateProjectsCount);

    return () => window.removeEventListener('resize', updateProjectsCount);
  }, []);


  // Guard so React Strict Mode (double-invoke in dev) does not trigger duplicate user_history calls
  const fetchRecentProjectsInFlightRef = useRef(false);

  // Fetch recent projects - using same filtering logic as ManagePage
  useEffect(() => {
    const fetchRecentProjects = async () => {
      if (!authCtx.token) return;
      if (fetchRecentProjectsInFlightRef.current) return;
      fetchRecentProjectsInFlightRef.current = true;

      setRecentProjectsLoading(true);
      try {
        const [singleResponse, bulkResponse] = await Promise.all([
          axios.get(`${API_URL}/auth/user_history?request_type=single_generate&limit=${projectsToShow}`, {
            headers: {
              'Authorization': `Bearer ${authCtx.token}`,
              'Content-Type': 'application/json'
            }
          }),
          axios.get(`${API_URL}/auth/user_history?request_type=bulk_generate&limit=${projectsToShow}`, {
            headers: {
              'Authorization': `Bearer ${authCtx.token}`,
              'Content-Type': 'application/json'
            }
          })
        ]);

        // Apply same filtering as ManagePage for single projects
        // Include drafts OR successful generations with content
        const validSingleProjects = (singleResponse.data?.data || []).filter(item => {
          // Include drafts
          if (item.is_draft || item.request_type === 'draft') {
            return true;
          }
          // Include successful generations that have content
          return item.status === 'Success' && (item.claude_output || item.output);
        }).map(item => ({
          ...item,
          projectType: 'single',
          created_at: item.created_at || item.createdAt
        }));

        // Apply same filtering as ManagePage for bulk projects
        // Include only completed bulk generations with output
        const validBulkProjects = (bulkResponse.data?.data || []).filter(item => {
          return item.status === 'Completed' && (item.image_urls || item.output_files);
        }).map(item => ({
          ...item,
          projectType: 'bulk',
          created_at: item.created_at || item.createdAt
        }));

        // Combine all projects, sort by most recent (date-desc), take top N based on screen size
        const allProjects = [...validSingleProjects, ...validBulkProjects]
          .filter(item => item.created_at)
          .sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return dateB - dateA; // date-desc: most recent first
          })
          .slice(0, projectsToShow);

        setRecentProjects(allProjects);
      } catch (error) {
        console.error('Error fetching recent projects:', error);
      } finally {
        setRecentProjectsLoading(false);
        fetchRecentProjectsInFlightRef.current = false;
      }
    };

    fetchRecentProjects();
  }, [authCtx.token, projectsToShow]);

  const handleCreateListingModalClose = () => {
    setIsCreateListingModalOpen(false);
    setModalInitialType(null); // Reset when closing
  };

  const handleCreateListingOption = (type, isNewListing, projectName) => {
    setIsCreateListingModalOpen(false);
    if (type === 'single') {
      navigate('/dashboard/generate/single', { 
        state: { 
          isNewListing: isNewListing,
          projectName: projectName 
        } 
      });
    } else if (type === 'bulk') {
      navigate('/dashboard/generate/bulk', { 
        state: { 
          isNewListing: isNewListing,
          projectName: projectName 
        } 
      });
    }
  };

  const handleUpgradeExistingListing = () => {
    // From dashboard card - skip to Single/Bulk directly (skip New/Existing step)
    setIsNewListingMode(false);
    setModalInitialType('existing'); // Set initial type to skip first step
    setIsCreateListingModalOpen(true);
  };
  
  return (
    <div className={styles.overviewContainer}>
      {/* <<< NO CHANGE NEEDED HERE >>> This will display the correct name */}
      <div className={styles.headSection}>
        <h1>Hey {fullName},</h1> 
      </div>

      {/* Quick Actions Box */}
      <div className={styles.quickActionsBox}>
        <svg width="14" height="15" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g opacity="0.7">
            <path d="M11.6985 10.1222C7.84504 9.252 7.24815 8.6551 6.37799 4.80155C6.33812 4.62542 6.18135 4.5 6.00019 4.5C5.81904 4.5 5.66227 4.62542 5.6224 4.80155C4.75185 8.6551 4.15535 9.252 0.301926 10.1222C0.125415 10.1625 0 10.3188 0 10.5C0 10.6812 0.125415 10.8375 0.301926 10.8778C4.15535 11.7484 4.75185 12.3453 5.6224 16.1985C5.66227 16.3746 5.81904 16.5 6.00019 16.5C6.18135 16.5 6.33812 16.3746 6.37799 16.1985C7.24854 12.3453 7.84504 11.7484 11.6985 10.8778C11.875 10.8375 12 10.6812 12 10.5C12 10.3188 11.8746 10.1625 11.6985 10.1222Z" fill="url(#paint0_linear_dashboard)"/>
            <path d="M15.6965 3.61959C13.635 3.15407 13.3455 2.86463 12.88 0.803467C12.8395 0.625828 12.6821 0.5 12.4998 0.5C12.3175 0.5 12.1601 0.625828 12.1196 0.803467C11.6541 2.86463 11.3646 3.15407 9.30347 3.61959C9.12583 3.66011 9 3.81749 9 3.99981C9 4.18212 9.12583 4.3395 9.30347 4.38002C11.3646 4.84554 11.6541 5.13498 12.1196 7.19653C12.1601 7.37378 12.3175 7.5 12.4998 7.5C12.6821 7.5 12.8395 7.37378 12.88 7.19653C13.3455 5.13498 13.635 4.84554 15.6965 4.38002C15.8738 4.3395 16 4.18212 16 3.99981C16 3.81749 15.8738 3.66011 15.6965 3.61959Z" fill="url(#paint1_linear_dashboard)"/>
          </g>
          <defs>
            <linearGradient id="paint0_linear_dashboard" x1="-4.47035e-08" y1="10.5" x2="11.0143" y2="16.5" gradientUnits="userSpaceOnUse">
              <stop stopColor="#2766AA"/>
              <stop offset="1" stopColor="#0752B0"/>
            </linearGradient>
            <linearGradient id="paint1_linear_dashboard" x1="9" y1="4" x2="15.425" y2="7.5" gradientUnits="userSpaceOnUse">
              <stop stopColor="#2766AA"/>
              <stop offset="1" stopColor="#0752B0"/>
            </linearGradient>
          </defs>
        </svg>
        <span className={styles.quickActionsText}>Quick Actions for you</span>
      </div>

      <div className={styles.mainContentGrid}>
        <FourCardGrid
          setIsNewListingMode={setIsNewListingMode}
          setModalInitialType={setModalInitialType}
          setIsCreateListingModalOpen={setIsCreateListingModalOpen}
          handleUpgradeExistingListing={handleUpgradeExistingListing}
        />
        <CatalogueHealthCard />
      </div>

      <RecentProjectsCard
        recentProjects={recentProjects}
        recentProjectsLoading={recentProjectsLoading}
      />

      {/* --- Create Listing Modal --- */}
      <CreateListingModal
        show={isCreateListingModalOpen}
        onClose={handleCreateListingModalClose}
        onOptionSelect={handleCreateListingOption}
        title={modalInitialType ? (modalInitialType === 'new' ? 'New Listing Creation' : 'Existing Listing Upgrade') : 'Create Listing'}
        initialType={modalInitialType}
      />
    </div>
  );
};

export default DashboardOverview;